import type { PromptOptionTemplate } from '../TeoApi';
import type { SelectedOptions } from './TeoText';

export function areAllPromptOptionsSelected(
  template: Maybe<PromptOptionTemplate>,
  selectedOptions: SelectedOptions,
): boolean {
  if (!template) return false;
  const allSelected = Object.entries(template.options).every(([categoryId, options]) => {
    if (options.length === 0) return true;
    const selected = selectedOptions[categoryId];
    if (!selected) return false;
    return options.some((option) => String(option.id) === selected);
  });

  return allSelected;
}

/**
 * Compares two selected-option maps for equality, ignoring key order and
 * treating a missing key the same as an `undefined` value.
 */
export function areSelectedOptionsEqual(
  a: Maybe<SelectedOptions>,
  b: Maybe<SelectedOptions>,
): boolean {
  const keys = new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})]);
  for (const key of keys) {
    if ((a?.[key] ?? undefined) !== (b?.[key] ?? undefined)) {
      return false;
    }
  }
  return true;
}

export function extractSelectedOptionsFromPromptTemplate(
  template: Maybe<PromptOptionTemplate>,
  selectedOptions: SelectedOptions,
): Record<string, string> {
  if (!template) {
    return {};
  }

  const selectedTemplateOptions = Object.entries(template.options)
    .map(([categoryId, options]) => {
      const selected = selectedOptions[categoryId];
      if (!selected) return undefined;
      const found = options.find((option) => String(option.id) === selected);
      if (!found) return undefined;
      return [categoryId, String(found.id)] as const;
    })
    .filter((x) => !!x);
  return Object.fromEntries(selectedTemplateOptions);
}
